import React, { useState } from 'react';
import API from '../utils/const';
import { authFetch } from '../utils/session';
import { Tooltip } from 'react-tooltip'
import LoadingOverlay from './LoadingOverlay';
import { useTranslation } from 'react-i18next';
import { Flex, Button, Accordion, Group, Text, Box, Popover, ActionIcon, Stack, Center, Loader } from '@mantine/core';
import { IconReload, IconTrash } from '@tabler/icons-react';
import { handleApiError } from '../utils/app';
import { createToast } from './Toast';
import { CalendarProvider, MessageKind } from '../utils/common';
import { ICSLogoSVG, GoogleSVG, OutlookSVG, AppleSVG } from './SVG';
import CalendarAccessWarning from './CalendarAccessWarning';
import ReconnectAccountModal from './ReconnectAccountModal';
import { ACCOUNT_DISCONNECTED_ERROR, getReconnectTooltipKey, isCalendarInaccessible } from '../utils/calendarAccess';
import { isSessionExpiredResponse, handleSessionExpired } from '../utils/session';

export interface Calendar {
    uuid: string;
    name: string;
    readonly: boolean;
    primary: boolean;
    platform_id: string;
    paused: string | null;
    paused_reason: string | null;
}

export interface Account {
    uuid: string;
    key: string;
    provider: CalendarProvider;
    calendars: Calendar[] | null;
}


const AccountCard: React.FC<{ account: Account; updateAccounts: () => void }> = ({ account, updateAccounts }) => {
    const { t } = useTranslation(['app']);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [reconnectModalOpen, setReconnectModalOpen] = useState(false);

    const openReconnectModal = () => setReconnectModalOpen(true);

    const handleRefreshError = async (response: Response) => {
        if (await isSessionExpiredResponse(response)) {
            await handleSessionExpired();
            return;
        }
        const error = await response.json();
        let msg = '';
        if (typeof error.message === 'string') {
            msg = error.message;
        } else if (typeof error.detail === 'string') {
            msg = error.detail;
        } else {
            msg = t('common.internal-server-error');
        }
        if (msg === ACCOUNT_DISCONNECTED_ERROR) {
            createToast(t('dashboard.calendar_inaccessible.reconnect_toast'), MessageKind.Error);
            openReconnectModal();
            // Server paused calendars; refresh props so isCalendarInaccessible updates.
            updateAccounts();
            return;
        }
        createToast(msg, MessageKind.Error);
    };

    const refreshCalendars = async () => {
        setIsLoading(true);
        try {
            const response = await authFetch(
                `${API}/accounts/${account.uuid}/calendars/refresh`,
                {
                    method: 'POST'
                }
            )
            if (!response.ok) {
                await handleRefreshError(response);
            }
            else {
                window.location.reload()
            }
        } finally {
            setIsLoading(false);
        }
    }

    const refreshCalendarEvents = async (calendar_uuid: string) => {
        setIsLoading(true);
        try {
            const response = await authFetch(
                `${API}/calendars/${calendar_uuid}/resync`,
                {
                    method: 'POST'
                }
            )
            if (response.ok) {
                createToast("All rules that use this calendar are going to be refreshed.", MessageKind.Info);
            } else {
                handleApiError(response, t, true)
            }
        } finally {
            setIsLoading(false);
        }
    }

    const deleteAccount = async (account_uuid: string) => {
        setIsLoading(true);
        try {
            const response = await authFetch(`${API}/accounts/${account_uuid}`, {
                method: 'DELETE'
            })
            if (response.ok) {
                window.location.reload()
            } else {
                handleApiError(response, t, true)
            }
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            {isLoading &&
                <LoadingOverlay />
            }
            <Accordion.Item key={account.uuid} value={account.uuid}>
                <Accordion.Control>
                    <Group>
                        <Box w={24} h={24}>
                            {account.provider === CalendarProvider.ICS && <ICSLogoSVG />}
                            {account.provider === CalendarProvider.GOOGLE && <GoogleSVG />}
                            {account.provider === CalendarProvider.OUTLOOK && <OutlookSVG />}
                            {account.provider === CalendarProvider.APPLE && <AppleSVG />}
                        </Box>
                        <Text fw={500} fz='md'>{account.key}</Text>
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Flex direction='column' gap='md' px='md'>
                        <Group justify='flex-end'>
                            {account.provider !== CalendarProvider.ICS &&
                                <Button onClick={refreshCalendars}>{t("dashboard.refresh-calendars")}</Button>
                            }
                            <Popover width={200} position="bottom" withArrow shadow="md">
                                <Popover.Target>
                                    <Button variant='outline' color='red' leftSection={<IconTrash stroke={1} />} onClick={() => deleteAccount(account.uuid)}>
                                        {t("dashboard.delete-account")}
                                    </Button>
                                </Popover.Target>
                                <Popover.Dropdown>
                                    <Stack gap='sm'>
                                        <Text size="sm">
                                            Warning: Make sure all synchronization rules are deleted, and all synchronized events have been deleted. It's advised to wait at least 15 minutes before deleting an account.
                                        </Text>
                                        <Button variant='outline' color='red' onClick={() => deleteAccount(account.uuid)}>Confirm</Button>
                                    </Stack>
                                </Popover.Dropdown>
                            </Popover>

                        </Group>

                        {account.calendars === null && (
                            <Center py="md">
                                <Loader size="sm" />
                            </Center>
                        )}

                        {account.calendars !== null && account.calendars.map((calendar) => (
                            <Group justify='space-between' key={calendar.uuid} align="flex-start">
                                <Stack gap={4}>
                                    <Text fw={500} fz='sm'>{calendar.name}</Text>
                                    {isCalendarInaccessible(calendar) &&
                                        <CalendarAccessWarning
                                            label={t('dashboard.calendar_inaccessible.label')}
                                            tooltip={t(getReconnectTooltipKey(account.provider))}
                                            reconnectLabel={t('dashboard.calendar_inaccessible.reconnect_link')}
                                            onReconnect={openReconnectModal}
                                        />
                                    }
                                </Stack>
                                <Button variant='outline' leftSection={<IconReload stroke={1} />} onClick={() => refreshCalendarEvents(calendar.uuid)}>
                                    {t("dashboard.refresh-events")}
                                </Button>
                            </Group>
                        ))}
                    </Flex>
                </Accordion.Panel>
            </Accordion.Item>
            <Tooltip style={{ zIndex: 999 }} anchorSelect='.google-calendar-tooltip'>This is a Google generated calendar. <br></br>It is read-only, so events can be copied from it, but not to it.</Tooltip>
            <ReconnectAccountModal
                opened={reconnectModalOpen}
                onClose={() => setReconnectModalOpen(false)}
                accountName={account.key}
                provider={account.provider}
            />
        </>
    );
};

export default AccountCard;
